import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../AuthProvider';
import './McpSetup.css';

const NODE_URL = process.env.REACT_APP_NODE_URL;

const McpSetup = () => {
  const { user, mongoUser, mongoUserId, isLoading, logout, openLogin, openSignup, openChangePlan, makeAuthenticatedRequest } = useAuth();

  const navigate = useNavigate();
  const mcpCallbackUrl = `${window.location.origin}/mcp`;

  const handleLogout = () => {
    logout();
    // Hard reload to /mcp so all state is fully reset
    window.location.href = '/mcp';
  };

  const handleLogin = () => {
    openLogin({ authenticationCallbackUrl: mcpCallbackUrl });
  };

  const handleSignup = () => {
    // Use Outseta embedded modal with MCP plan pre-selected
    if (window.Outseta) {
      window.Outseta.auth.open({
        widgetMode: 'register',
        planUid: 'EWBwelmr',
        planPaymentTerm: 'month',
        skipPlanOptions: true,
        authenticationCallbackUrl: mcpCallbackUrl,
      });
    }
  };

  const [apiKey, setApiKey] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [showManualSetup, setShowManualSetup] = useState(false);

  const isAuthenticated = !!user && !!mongoUserId;

  const fetchOrCreateApiKey = useCallback(async () => {
    if (!mongoUserId) return;
    setApiKeyLoading(true);
    try {
      const res = await makeAuthenticatedRequest(`${NODE_URL}/user_data/mcp-api-key`, {
        method: 'POST',
        body: JSON.stringify({ userId: mongoUserId }),
      });
      const data = await res.json();
      if (data.apiKey) setApiKey(data.apiKey);
    } catch (err) {
      console.error('Failed to fetch API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  }, [mongoUserId, makeAuthenticatedRequest]);

  const fetchAccountInfo = useCallback(async () => {
    if (!mongoUserId) return;
    try {
      const res = await makeAuthenticatedRequest(`${NODE_URL}/user_data/mcp-account?userId=${mongoUserId}`);
      const data = await res.json();
      setAccountInfo(data);
    } catch (err) {
      console.error('Failed to fetch account info:', err);
    }
  }, [mongoUserId, makeAuthenticatedRequest]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrCreateApiKey();
      fetchAccountInfo();
    }
  }, [isAuthenticated, fetchOrCreateApiKey, fetchAccountInfo]);

  const handleRegenerate = async () => {
    if (!window.confirm('This will invalidate your current API key. Any configured MCP servers will need to be updated. Continue?')) return;
    setApiKeyLoading(true);
    try {
      const res = await makeAuthenticatedRequest(`${NODE_URL}/user_data/mcp-api-key/regenerate`, {
        method: 'POST',
        body: JSON.stringify({ userId: mongoUserId }),
      });
      const data = await res.json();
      if (data.apiKey) setApiKey(data.apiKey);
    } catch (err) {
      console.error('Failed to regenerate API key:', err);
    } finally {
      setApiKeyLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const usedToolCalls = accountInfo?.mcpToolCalls ? (accountInfo.mcpToolCalls.total - accountInfo.mcpToolCalls.remaining) : 0;

  const claudeDesktopConfig = apiKey ? JSON.stringify({
    mcpServers: {
      vestmap: {
        command: 'npx',
        args: ['-y', 'vestmap-mcp'],
        env: { VESTMAP_API_KEY: apiKey },
      },
    },
  }, null, 2) : '';

  if (isLoading) {
    return (
      <div className="mcp-page">
        <div className="mcp-container">
          <div className="mcp-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-page">
      <div className="mcp-container">
        {/* Back to VestMap */}
        <a
          href="/run-reports"
          className="mcp-back-link"
        >
          &larr; Return back to VestMap
        </a>

        {/* Header */}
        <div className="mcp-header">
          <div className="mcp-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#4FA490" />
              <path d="M8 16L14 22L24 10" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h1>VestMap AI Integration</h1>
          </div>
          <p className="mcp-subtitle">
            On-demand location intelligence for real estate — powered by ESRI, AGS, FHFA, FEMA, and US Census data
          </p>
        </div>

        {!isAuthenticated ? (
          /* ───────── NOT LOGGED IN ───────── */
          <div className="mcp-auth-section">
            <div className="mcp-features">
              <h2>What you get</h2>
              <div className="mcp-feature-grid">
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f4ca;</span>
                  <h3>Full Property Reports</h3>
                  <p>Demographics, population, income, median rent, home values, crime indexes, schools, employment, and growth projections</p>
                </div>
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f30d;</span>
                  <h3>1000+ GIS Data Fields</h3>
                  <p>Query ESRI, AGS, FHFA HPI, FEMA NRI, and Census data at block, tract, ZIP, county, CBSA, or state level</p>
                </div>
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f916;</span>
                  <h3>AI-Powered Analysis</h3>
                  <p>Generate OMs, run site selection, underwrite properties, compare markets, and assess climate risk</p>
                </div>
                <div className="mcp-feature-card">
                  <span className="mcp-feature-icon">&#x1f4ac;</span>
                  <h3>Works with Your AI</h3>
                  <p>Claude Desktop, Claude Code, ChatGPT, Codex — ask follow-up questions, compare addresses, export data</p>
                </div>
              </div>
            </div>

            <div className="mcp-auth-buttons">
              <button className="mcp-btn mcp-btn-primary" onClick={handleSignup}>
                Create Account
              </button>
              <button className="mcp-btn mcp-btn-secondary" onClick={handleLogin}>
                Sign In
              </button>
            </div>

            <p className="mcp-auth-note">
              Already have a VestMap account? Sign in to get your API key.
            </p>
          </div>
        ) : (
          /* ───────── LOGGED IN ───────── */
          <div className="mcp-dashboard">
            {/* Welcome + Logout */}
            <div className="mcp-welcome-bar">
              <div className="mcp-welcome">
                <h2>
                  Welcome, {mongoUser?.firstname || user?.given_name || 'there'}
                </h2>
                <span className="mcp-account-plan">
                  {accountInfo?.plan || '...'}
                </span>
              </div>
              <button className="mcp-btn-logout" onClick={handleLogout}>
                Sign Out
              </button>
            </div>

            {/* ── Your API Key (prominent) ── */}
            <div className="mcp-apikey-section">
              <h2>Your API Key</h2>
              <p className="mcp-apikey-desc">Copy this key - you'll need it when installing the extension below.</p>
              <div className="mcp-apikey-box">
                {apiKeyLoading ? (
                  <div className="mcp-apikey-loading">Generating key...</div>
                ) : apiKey ? (
                  <>
                    <code className="mcp-apikey-code">{apiKey}</code>
                    <div className="mcp-apikey-actions">
                      <button
                        className="mcp-btn-copy-key"
                        onClick={() => copyToClipboard(apiKey, 'key')}
                      >
                        {copied === 'key' ? 'Copied!' : 'Copy Key'}
                      </button>
                      <button className="mcp-btn-regen" onClick={handleRegenerate}>
                        Regenerate
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="mcp-apikey-loading">No key generated</div>
                )}
              </div>
            </div>

            {/* ── Connect to AI Tools ── */}
            <div className="mcp-connect-section">
              <h2>Connect to AI</h2>

              <div className="mcp-connect-grid">
                {/* Claude Desktop - ACTIVE */}
                <div className="mcp-connect-card mcp-connect-card-active">
                  <div className="mcp-connect-icon">
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <rect width="36" height="36" rx="8" fill="#D97757"/>
                      <path d="M10 18h16M18 10v16" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="mcp-connect-info">
                    <div className="mcp-connect-label">Claude Desktop</div>
                    <div className="mcp-connect-status">Available now</div>
                  </div>
                  <div className="mcp-connect-actions">
                    <a
                      href="/vestmap-mcp-1.8.0.mcpb"
                      className="mcp-btn-install"
                      onClick={() => {
                        if (apiKey) {
                          navigator.clipboard.writeText(apiKey);
                          setCopied('auto');
                          setTimeout(() => setCopied(null), 4000);
                        }
                      }}
                    >
                      Install Extension
                    </a>
                  </div>
                  {copied === 'auto' && (
                    <div className="mcp-connect-hint">
                      API key copied to clipboard — paste it when prompted during install
                    </div>
                  )}
                  <div className="mcp-connect-steps">
                    <span>1. Click "Install Extension" (API key auto-copied!)</span>
                    <span>2. Double-click the downloaded <code>.mcpb</code> file — Claude Desktop opens the installer</span>
                    <span>3. Paste your API key when prompted, then click Save</span>
                  </div>
                  <button
                    className="mcp-btn-alt-setup"
                    onClick={() => setShowManualSetup(!showManualSetup)}
                  >
                    {showManualSetup ? 'Hide manual setup' : 'Or configure manually'}
                  </button>
                  {showManualSetup && (
                    <div className="mcp-manual-setup">
                      <p>In Claude Desktop, go to <strong>Settings &rarr; Extensions &rarr; Advanced Settings</strong>, then add this to your config:</p>
                      <div className="mcp-code-block">
                        <pre>{claudeDesktopConfig}</pre>
                        <button className="mcp-btn-copy" onClick={() => copyToClipboard(claudeDesktopConfig, 'config')}>
                          {copied === 'config' ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Claude Code - AVAILABLE */}
                <div className="mcp-connect-card mcp-connect-card-active mcp-connect-card-compact">
                  <div className="mcp-connect-icon">
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <rect width="36" height="36" rx="8" fill="#1a1a2e"/>
                      <path d="M12 12l-5 6 5 6M24 12l5 6-5 6" stroke="#D97757" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15 18h6" stroke="#D97757" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <div className="mcp-connect-info">
                    <div className="mcp-connect-label">Claude Code</div>
                    <div className="mcp-connect-status">Available now</div>
                  </div>
                  <div className="mcp-connect-actions">
                    <button
                      className="mcp-btn-install"
                      onClick={() => {
                        if (apiKey) {
                          navigator.clipboard.writeText(`npx vestmap-mcp setup ${apiKey}`);
                          setCopied('claude-code-cmd');
                          setTimeout(() => setCopied(null), 3000);
                        }
                      }}
                    >
                      {copied === 'claude-code-cmd' ? 'Copied to clipboard!' : 'Copy Install Command'}
                    </button>
                  </div>
                  {copied === 'claude-code-cmd' && (
                    <div className="mcp-connect-hint">
                      Paste this in your terminal to configure Claude Code
                    </div>
                  )}
                  <div className="mcp-connect-steps">
                    <span>1. Copy command</span>
                    <span>2. Paste in terminal</span>
                    <span>3. Start a new Claude Code session</span>
                  </div>
                </div>

                {/* OpenAI Codex - AVAILABLE */}
                <div className="mcp-connect-card mcp-connect-card-active mcp-connect-card-compact mcp-connect-card-green">
                  <div className="mcp-connect-icon">
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <rect width="36" height="36" rx="8" fill="#0d3320"/>
                      <circle cx="18" cy="18" r="8" stroke="#10a37f" strokeWidth="2"/>
                      <path d="M18 13v5l3 3" stroke="#10a37f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="mcp-connect-info">
                    <div className="mcp-connect-label">OpenAI Codex</div>
                    <div className="mcp-connect-status">Available now</div>
                  </div>
                  <div className="mcp-connect-actions">
                    <button
                      className="mcp-btn-install"
                      onClick={() => {
                        if (apiKey) {
                          navigator.clipboard.writeText(`codex mcp add vestmap --env VESTMAP_API_KEY=${apiKey} -- npx -y vestmap-mcp`);
                          setCopied('codex-cmd');
                          setTimeout(() => setCopied(null), 3000);
                        }
                      }}
                    >
                      {copied === 'codex-cmd' ? 'Copied to clipboard!' : 'Copy Install Command'}
                    </button>
                  </div>
                  {copied === 'codex-cmd' && (
                    <div className="mcp-connect-hint">
                      Paste this in your terminal to configure Codex
                    </div>
                  )}
                  <div className="mcp-connect-steps">
                    <span>1. Copy command</span>
                    <span>2. Paste in terminal</span>
                    <span>3. Start a new Codex session</span>
                  </div>
                </div>

                {/* ChatGPT - COMING SOON */}
                <div className="mcp-connect-card mcp-connect-card-disabled">
                  <div className="mcp-connect-icon">
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <rect width="36" height="36" rx="8" fill="#0d3320"/>
                      <circle cx="18" cy="18" r="8" stroke="#10a37f" strokeWidth="2" opacity="0.5"/>
                      <path d="M15 15h6M15 18h4M15 21h6" stroke="#10a37f" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
                    </svg>
                  </div>
                  <div className="mcp-connect-info">
                    <div className="mcp-connect-label">ChatGPT</div>
                    <div className="mcp-connect-coming-soon">Coming Soon</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CLI Install */}
            <div className="mcp-cli-section">
              <h2>Install via Terminal</h2>
              <div className="mcp-cli-commands">
                <div className="mcp-cli-row">
                  <span className="mcp-cli-label">Claude Code:</span>
                  <div className="mcp-code-block">
                    <pre>{apiKey ? `npx vestmap-mcp setup ${apiKey}` : 'npx vestmap-mcp setup YOUR_API_KEY'}</pre>
                    <button className="mcp-btn-copy" onClick={() => copyToClipboard(`npx vestmap-mcp setup ${apiKey}`, 'cli-code')}>
                      {copied === 'cli-code' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
                <div className="mcp-cli-row">
                  <span className="mcp-cli-label">Claude Desktop:</span>
                  <div className="mcp-code-block">
                    <pre>{apiKey ? `npx vestmap-mcp setup ${apiKey} --desktop` : 'npx vestmap-mcp setup YOUR_API_KEY --desktop'}</pre>
                    <button className="mcp-btn-copy" onClick={() => copyToClipboard(`npx vestmap-mcp setup ${apiKey} --desktop`, 'cli-desktop')}>
                      {copied === 'cli-desktop' ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* MCP Credits */}
            <div className="mcp-credits-section">
              <h2>MCP Credits</h2>
              <div className="mcp-credits-grid">
                <div className="mcp-credit-card">
                  <div className="mcp-credit-header">Tool Calls</div>
                  <div className="mcp-credit-numbers">
                    <span className="mcp-credit-remaining">{accountInfo?.mcpToolCalls?.remaining ?? '...'}</span>
                    <span className="mcp-credit-total">/ {accountInfo?.mcpToolCalls?.total ?? '...'}</span>
                  </div>
                  <div className="mcp-credit-bar">
                    <div
                      className="mcp-credit-bar-fill"
                      style={{ width: accountInfo?.mcpToolCalls?.total ? `${(accountInfo.mcpToolCalls.remaining / accountInfo.mcpToolCalls.total) * 100}%` : '0%' }}
                    />
                  </div>
                  <div className="mcp-credit-label">{usedToolCalls} used this period</div>
                </div>

                <div className="mcp-credit-card mcp-credit-card-cta">
                  <div className="mcp-credit-header">Need more?</div>
                  <p className="mcp-credit-desc">Add 1,000 tool calls to your account ($49 one-time)</p>
                  <button
                    className="mcp-btn-upgrade"
                    onClick={() => {
                      if (window.Outseta) {
                        window.Outseta.profile.open({ tab: 'plan' });
                      }
                    }}
                  >
                    Buy 1,000 Credits
                  </button>
                  <div className="mcp-credit-steps">
                    <span>1. Click "Buy 1,000 Credits"</span>
                    <span>2. Click "Edit options" on your plan</span>
                    <span>3. Select "VestMap MCP Credits"</span>
                    <span>4. Complete purchase</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="mcp-skills-section">
              <h2>AI Skills (Optional)</h2>
              <p className="mcp-skills-desc">
                Install these skills in Claude Desktop for optimized property research and professional document generation.
              </p>

              <div className="mcp-skills-grid">
                <div className="mcp-skill-card">
                  <div className="mcp-skill-info">
                    <h3><a href="https://github.com/clayripma/vestmap-skill" target="_blank" rel="noopener noreferrer" className="mcp-skill-title-link">VestMap Research Skill</a></h3>
                    <p>Optimized prompts for property data queries, rankings, comparisons, and due diligence research.</p>
                  </div>
                  <a href="/skills/vestmap-skill.zip" download className="mcp-btn-skill-download">
                    Download Skill
                  </a>
                </div>

                <div className="mcp-skill-card">
                  <div className="mcp-skill-info">
                    <h3><a href="https://github.com/clayripma/OM-Location-Pages" target="_blank" rel="noopener noreferrer" className="mcp-skill-title-link">OM Location Pages Skill</a></h3>
                    <p>Generate professional Offering Memorandum location pages with demographics, income, housing, and safety data.</p>
                  </div>
                  <a href="/skills/om-location-pages.zip" download className="mcp-btn-skill-download">
                    Download Skill
                  </a>
                </div>
              </div>

              <div className="mcp-skills-install-steps">
                <h3>How to install skills in Claude Desktop</h3>
                <ol>
                  <li>Open Claude Desktop &gt; <strong>Settings</strong> &gt; <strong>Connectors</strong> &gt; <strong>Customize</strong></li>
                  <li>Click <strong>Skills</strong> in the left sidebar</li>
                  <li>Click the <strong>+</strong> icon &gt; <strong>Create skill</strong> &gt; <strong>Upload a skill</strong></li>
                  <li>Select the downloaded <code>.zip</code> file</li>
                  <li>Repeat for the second skill</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default McpSetup;
